/**
 * Shared input validators for the public free-tool lead forms.
 *
 * Centralised so every tool gates email + phone the same way. Two
 * problems these fix:
 *   1. Email checks that only looked for "@" let "test@", trailing-dot,
 *      and whitespace-padded addresses through, producing unreachable
 *      leads + bounced delivery emails. They also let the per-email
 *      lifetime cap be bypassed with whitespace tricks ("a@b.com " vs
 *      "a@b.com") since the cap keys on the raw string.
 *   2. Phone checks that only confirmed the field was non-empty accepted
 *      "abc123" for required Indian numbers.
 *
 * Keep these permissive enough not to reject real international formats,
 * strict enough to catch obvious garbage. Always TRIM before validating
 * and before sending, so the value that reaches the backend is the same
 * value the cap is keyed on.
 */

/** Trim + lowercase an email for consistent storage + cap-keying. */
export function normalizeEmail(raw: string): string {
  return (raw || '').trim().toLowerCase();
}

/**
 * Pragmatic email check: local-part, single @, domain with at least one
 * dot and a 2+ char TLD. Rejects "test@", "a@b", trailing/leading spaces,
 * leading/trailing dots, and consecutive dots ("a..b@x.com"). Not a full
 * RFC 5322 validator (those reject real addresses), just enough to stop
 * garbage + bounces.
 *
 * Dot rules enforced explicitly because the simpler "[^\s@]+" character
 * class happily matches dots, so it let "user..name@x.com", ".user@x.com",
 * and "user@x.com." through.
 */
export function isValidEmail(raw: string): boolean {
  const email = normalizeEmail(raw);
  if (!email || email.length > 254) return false;
  // no spaces, exactly one @, domain has a dot + 2+ char alpha TLD.
  // Local + domain labels are dot-separated runs of allowed chars, so a
  // leading/trailing/double dot can't appear in either part.
  const re = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
  return re.test(email);
}

/**
 * Phone check for required (India) capture. Strips formatting characters
 * (spaces, dashes, parens, leading +) and requires 7-15 remaining digits
 * (E.164 range). Rejects "abc123", "12", and empty.
 */
export function isValidPhone(raw: string): boolean {
  const trimmed = (raw || '').trim();
  if (!trimmed) return false;
  const digits = trimmed.replace(/[\s\-().]/g, '').replace(/^\+/, '');
  return /^\d{7,15}$/.test(digits);
}

/**
 * Best-effort reachability check for a freshly-uploaded PDF URL before we
 * show the "ready, download" card. A silently-failed storage upload
 * returns a URL string that 404s; without this the user only discovers
 * the breakage after clicking Download. Uses HEAD, falls back to GET if
 * HEAD is rejected. Network errors resolve to `true` (over-deliver beats
 * over-block — the real download will surface any error itself), but a
 * definite 4xx/5xx resolves to `false`.
 */
export async function isPdfReachable(url: string): Promise<boolean> {
  if (!url) return false;
  try {
    const resp = await fetch(url, { method: 'HEAD' });
    if (resp.ok) return true;
    // Some storage CDNs reject HEAD with 400/405 but serve GET fine.
    if (resp.status === 405 || resp.status === 400) {
      const getResp = await fetch(url, { method: 'GET' });
      return getResp.ok;
    }
    return false;
  } catch {
    // Network/CORS error — don't block the user on an ambiguous failure.
    return true;
  }
}
